import React, { useCallback, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaFileExcel,
  FaSpinner,
  FaUpload,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../services/axios";
import "./modalImportarConvidadosExcel.css";

const STATUS_LABEL = {
  valido: "Será criado",
  email_ja_existe: "E-mail já existe",
  email_invalido: "E-mail inválido",
  campos_obrigatorios: "Campos obrigatórios",
  email_duplicado_arquivo: "Duplicado no arquivo",
  criado: "Criado",
  ignorado: "Ignorado",
  erro: "Erro",
};

/**
 * Modal em 3 etapas: upload → confirmação → resultado.
 */
const ModalImportarConvidadosExcel = ({ show, onHide, onImportado }) => {
  const inputRef = useRef(null);
  const [etapa, setEtapa] = useState("upload"); // upload | confirmacao | resultado
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState([]);
  const [resumoPreview, setResumoPreview] = useState(null);
  const [resultado, setResultado] = useState([]);
  const [resumoResultado, setResumoResultado] = useState(null);
  const [isAnalisando, setIsAnalisando] = useState(false);
  const [isConfirmando, setIsConfirmando] = useState(false);

  const resetar = useCallback(() => {
    setEtapa("upload");
    setArquivo(null);
    setPreview([]);
    setResumoPreview(null);
    setResultado([]);
    setResumoResultado(null);
    setIsAnalisando(false);
    setIsConfirmando(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const fechar = useCallback(() => {
    if (isAnalisando || isConfirmando) return;
    resetar();
    onHide?.();
  }, [isAnalisando, isConfirmando, onHide, resetar]);

  const analisarArquivo = useCallback(async (file) => {
    if (!file) return;

    const nome = String(file.name || "").toLowerCase();
    if (!nome.endsWith(".xlsx") && !nome.endsWith(".xls")) {
      toast.error("Envie um arquivo Excel (.xlsx ou .xls).");
      return;
    }

    setArquivo(file);
    setIsAnalisando(true);

    try {
      const formData = new FormData();
      formData.append("arquivo", file);

      const resp = await api.post(
        "/usuarios-import-convidados-excel-preview",
        formData,
        {
          headers: { "content-type": "multipart/form-data" },
        },
      );

      setPreview(resp.data?.dados || []);
      setResumoPreview(resp.data?.resumo || null);
      setEtapa("confirmacao");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao analisar o arquivo Excel.",
      );
      setArquivo(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setIsAnalisando(false);
    }
  }, []);

  const onSelectFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) analisarArquivo(file);
    },
    [analisarArquivo],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isAnalisando || isConfirmando) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) analisarArquivo(file);
    },
    [analisarArquivo, isAnalisando, isConfirmando],
  );

  const confirmarImportacao = useCallback(async () => {
    if (!arquivo) return;
    setIsConfirmando(true);

    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);

      const resp = await api.post(
        "/usuarios-import-convidados-excel-confirmar",
        formData,
        {
          headers: { "content-type": "multipart/form-data" },
        },
      );

      setResultado(resp.data?.dados || []);
      setResumoResultado(resp.data?.resumo || null);
      setEtapa("resultado");
      toast.success(
        `Importação concluída: ${resp.data?.resumo?.criados ?? 0} criado(s).`,
      );
      onImportado?.();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao confirmar a importação.",
      );
    } finally {
      setIsConfirmando(false);
    }
  }, [arquivo, onImportado]);

  const titulo =
    etapa === "upload"
      ? "Importar convidados (Excel)"
      : etapa === "confirmacao"
        ? "Confirmar importação"
        : "Resultado da importação";

  const linhasTabela = etapa === "resultado" ? resultado : preview;

  return (
    <Modal
      show={show}
      onHide={fechar}
      centered
      className={`mice-modal${etapa !== "upload" ? " mice-modal--wide" : ""}`}
      backdrop={isAnalisando || isConfirmando ? "static" : true}
    >
      <Modal.Header closeButton={!isAnalisando && !isConfirmando}>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {etapa === "upload" ? (
          <div className="mice-upload">
            <p className="mice-cols">
              Colunas: <strong>EXECUTIVO</strong>, <strong>CARGO</strong>,{" "}
              <strong>Instituição</strong>, <strong>E-MAIL</strong>,{" "}
              <strong>CELULAR</strong>
            </p>
            <p className="mice-hint">
              Na importação, e-mails já cadastrados são ignorados.
            </p>

            <div
              className={`mice-dropzone${isAnalisando ? " mice-dropzone--busy" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={onDrop}
              onClick={() => !isAnalisando && inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              aria-label="Selecionar arquivo Excel"
            >
              {isAnalisando ? (
                <>
                  <FaSpinner className="fa-spin mice-icon" aria-hidden />
                  <span>Analisando arquivo…</span>
                </>
              ) : (
                <>
                  <FaUpload className="mice-icon" aria-hidden />
                  <span>Arraste o Excel ou clique para selecionar</span>
                  <span className="mice-dropzone-sub">.xlsx ou .xls · até 10 MB</span>
                </>
              )}
            </div>

            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={onSelectFile}
              hidden
            />
          </div>
        ) : null}

        {etapa === "confirmacao" || etapa === "resultado" ? (
          <div className="mice-review">
            {etapa === "confirmacao" && resumoPreview ? (
              <div className="mice-resumo" aria-label="Resumo do preview">
                <div className="mice-resumo-item">
                  <span className="mice-resumo-value">{resumoPreview.total}</span>
                  <span className="mice-resumo-label">Total</span>
                </div>
                <div className="mice-resumo-item mice-resumo-item--ok">
                  <span className="mice-resumo-value">{resumoPreview.validos}</span>
                  <span className="mice-resumo-label">Serão criados</span>
                </div>
                <div className="mice-resumo-item">
                  <span className="mice-resumo-value">
                    {resumoPreview.email_ja_existe}
                  </span>
                  <span className="mice-resumo-label">Já existem</span>
                </div>
                <div className="mice-resumo-item mice-resumo-item--warn">
                  <span className="mice-resumo-value">{resumoPreview.invalidos}</span>
                  <span className="mice-resumo-label">Inválidos</span>
                </div>
              </div>
            ) : null}

            {etapa === "resultado" && resumoResultado ? (
              <div className="mice-resumo" aria-label="Resumo do resultado">
                <div className="mice-resumo-item">
                  <span className="mice-resumo-value">{resumoResultado.total}</span>
                  <span className="mice-resumo-label">Total</span>
                </div>
                <div className="mice-resumo-item mice-resumo-item--ok">
                  <span className="mice-resumo-value">{resumoResultado.criados}</span>
                  <span className="mice-resumo-label">Criados</span>
                </div>
                <div className="mice-resumo-item">
                  <span className="mice-resumo-value">
                    {resumoResultado.ignorados}
                  </span>
                  <span className="mice-resumo-label">Ignorados</span>
                </div>
                <div className="mice-resumo-item mice-resumo-item--warn">
                  <span className="mice-resumo-value">{resumoResultado.erros}</span>
                  <span className="mice-resumo-label">Erros</span>
                </div>
              </div>
            ) : null}

            {etapa === "confirmacao" ? (
              <p className="mice-hint mice-hint--confirm">
                Confirma o carregamento de{" "}
                <strong>{resumoPreview?.validos ?? 0}</strong> novo(s)
                convidado(s)?
              </p>
            ) : null}

            <div className="mice-table-wrap">
              <table className="mice-table">
                <thead>
                  <tr>
                    <th>Linha</th>
                    <th>Executivo</th>
                    <th>Instituição</th>
                    <th>E-mail</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasTabela.map((row) => (
                    <tr key={`${row.linha}-${row.email}`}>
                      <td>{row.linha}</td>
                      <td>{row.executivo || "—"}</td>
                      <td>{row.instituicao || "—"}</td>
                      <td className="mice-cell-email">{row.email || "—"}</td>
                      <td>
                        <span
                          className={`mice-status mice-status--${row.status}`}
                          title={row.mensagem}
                        >
                          {STATUS_LABEL[row.status] || row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="mice-footer">
        {etapa === "upload" ? (
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={fechar}
            disabled={isAnalisando}
          >
            Cancelar
          </button>
        ) : null}

        {etapa === "confirmacao" ? (
          <>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={resetar}
              disabled={isConfirmando}
            >
              <FaTimes size={14} aria-hidden />
              Escolher outro arquivo
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={confirmarImportacao}
              disabled={
                isConfirmando || !resumoPreview || resumoPreview.validos === 0
              }
            >
              {isConfirmando ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaCheck size={14} aria-hidden />
              )}
              {isConfirmando ? "Importando…" : "Confirmar carregamento"}
            </button>
          </>
        ) : null}

        {etapa === "resultado" ? (
          <button
            type="button"
            className="apl-btn apl-btn--primary"
            onClick={fechar}
          >
            <FaFileExcel size={14} aria-hidden />
            Fechar
          </button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalImportarConvidadosExcel;
